import { useEffect, useMemo, useState } from 'react';
import { BY_SYMBOL } from '../elements';
import {
  VizCard,
  NowYouTry,
  DARK_INK,
  usePrefersReducedMotion,
  cueIsActive,
  cueHighlights,
  type VizCue,
} from '../vizShared';

// IsotopeNotation — the nuclear (A/Z) symbol notation rendered as a real
// diagram: the big element symbol with mass number A as a top-left superscript
// and atomic number Z as a bottom-left subscript, a labelled count breakdown
// (protons = Z, neutrons = A − Z, electrons = Z adjusted for charge), and a
// small nucleus drawing packed with proton/neutron nucleons.
// config: { symbol?, mass? (A), charge?, title? }.
// Cue: cue.step walks the reveal A → Z → protons → neutrons (→ electrons);
//      cue.highlight ("a" | "z" | "protons" | "neutrons" | "electrons" | a
//      symbol) spotlights one part.

export interface IsotopeNotationConfig {
  /** Element symbol, e.g. "C", "U", "Na". */
  symbol?: string;
  /** Mass number A (protons + neutrons). Defaults to the rounded atomic mass. */
  mass?: number;
  /** Net charge: 0 neutral, +2 cation, −1 anion, … */
  charge?: number;
  title?: string;
  cue_steps?: unknown;
}

interface Props {
  config?: IsotopeNotationConfig;
  onComplete?: (result?: unknown) => void;
  cue?: VizCue;
}

// The four parts revealed in order. Electrons piggy-back on the proton step but
// are highlightable on their own.
type Part = 'a' | 'z' | 'protons' | 'neutrons' | 'electrons';
const REVEAL_ORDER: Part[] = ['a', 'z', 'protons', 'neutrons'];

// Format a charge as a superscript label ("2+", "−", ""), never "0".
function chargeLabel(charge: number): string {
  if (!charge) return '';
  const mag = Math.abs(charge);
  const sign = charge > 0 ? '+' : '−';
  return mag === 1 ? sign : `${mag}${sign}`;
}

// Lay nucleons out on a rough hexagonal-ish ring packing so the nucleus reads as
// a cluster rather than a grid. Deterministic (no animation jitter).
function packNucleons(count: number, radius: number): Array<{ x: number; y: number }> {
  const pts: Array<{ x: number; y: number }> = [];
  if (count <= 0) return pts;
  pts.push({ x: 0, y: 0 });
  let placed = 1;
  let ring = 1;
  while (placed < count) {
    const perRing = Math.min(count - placed, ring * 6);
    const r = (radius * ring) / Math.ceil(Math.cbrt(count));
    for (let i = 0; i < perRing; i++) {
      const a = (i / perRing) * Math.PI * 2 + ring * 0.6;
      pts.push({ x: Math.cos(a) * r, y: Math.sin(a) * r });
    }
    placed += perRing;
    ring += 1;
  }
  return pts;
}

export default function IsotopeNotation({ config = {}, onComplete, cue }: Props) {
  const reduced = usePrefersReducedMotion();
  const hasCue = cueIsActive(cue);
  const highlights = cueHighlights(cue);

  // Resolve element from symbol (default Carbon-14 so the demo is recognisable).
  const symbol = (config.symbol ?? 'C').trim();
  const el = BY_SYMBOL[symbol] ?? BY_SYMBOL.C;
  const z = el.z;
  // A defaults to the rounded atomic mass; clamp so neutrons can't go negative.
  const a = Math.max(z, Math.round(config.mass ?? el.mass));
  const charge = Math.trunc(config.charge ?? 0);

  const neutrons = a - z;
  const protons = z;
  const electrons = z - charge; // cation (+) loses electrons, anion (−) gains

  // How many parts are revealed (index into REVEAL_ORDER + 1). Fully revealed at
  // >= REVEAL_ORDER.length.
  const [revealed, setRevealed] = useState<number>(reduced || hasCue ? REVEAL_ORDER.length : 0);
  const [showTry, setShowTry] = useState(false);
  const [interacted, setInteracted] = useState(false);

  // Self-demo: reveal parts one at a time on mount (unless reduced / cue-driven).
  useEffect(() => {
    if (reduced || hasCue) {
      setRevealed(REVEAL_ORDER.length);
      return;
    }
    setRevealed(0);
    const timers: number[] = [];
    REVEAL_ORDER.forEach((_, i) =>
      timers.push(window.setTimeout(() => setRevealed(i + 1), 520 * (i + 1))),
    );
    timers.push(
      window.setTimeout(() => setShowTry(true), 520 * (REVEAL_ORDER.length + 1)),
    );
    return () => timers.forEach((t) => window.clearTimeout(t));
    // Arm once on mount; element changes are reflected by the values, not re-demo.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [symbol, a, charge]);

  // Cue: step walks the same reveal order; progress reveals proportionally.
  useEffect(() => {
    if (!cue) return;
    setShowTry(false);
    // Reveal parts one-by-one only while PLAYING (step or progress); at rest
    // (paused) show the full nuclear symbol + breakdown, never a bare symbol.
    if (cue.play === true && typeof cue.step === 'number') {
      setRevealed(Math.max(0, Math.min(REVEAL_ORDER.length, Math.round(cue.step) + 1)));
    } else if (cue.play === true && typeof cue.progress === 'number') {
      const p = Math.max(0, Math.min(1, cue.progress));
      setRevealed(Math.round(p * REVEAL_ORDER.length));
    } else {
      setRevealed(REVEAL_ORDER.length);
    }
  }, [cue]);

  // A part is "lit" if (a) cue highlights it explicitly, or (b) no highlight is
  // active and it's been revealed. Highlight tokens accept the part name or the
  // element symbol (which lights the whole notation).
  const symbolHinted = highlights.includes(el.symbol.toLowerCase());
  const isLit = (part: Part, revealIdx: number): boolean => {
    if (highlights.length) return highlights.includes(part) || symbolHinted;
    return revealed > revealIdx;
  };
  const shown = (revealIdx: number): boolean => revealed > revealIdx || revealed >= REVEAL_ORDER.length;

  const reveal = () => {
    if (revealed < REVEAL_ORDER.length) setRevealed(REVEAL_ORDER.length);
    setShowTry(false);
    if (!interacted) {
      setInteracted(true);
      onComplete?.({ type: 'isotope_notation', symbol: el.symbol, a, z, protons, neutrons, electrons, charge });
    }
  };

  const nucleus = useMemo(() => {
    const all = packNucleons(protons + neutrons, 16);
    // Assign the inner nucleons to protons first so the cluster shows both.
    return all.map((p, i) => ({ ...p, kind: i < protons ? ('p' as const) : ('n' as const) }));
  }, [protons, neutrons]);

  const chLabel = chargeLabel(charge);

  // Reusable count-row renderer.
  const CountRow = ({
    part,
    revealIdx,
    label,
    value,
    formula,
    color,
  }: {
    part: Part;
    revealIdx: number;
    label: string;
    value: number;
    formula: string;
    color: string;
  }) => {
    const lit = isLit(part, revealIdx);
    const visible = shown(revealIdx);
    return (
      <div
        className="flex items-center justify-between gap-2 rounded-md border px-3 py-2 transition-all"
        style={{
          borderColor: lit ? color : 'var(--ws-border)',
          background: lit ? `color-mix(in srgb, ${color} 14%, transparent)` : 'var(--ws-surface-2)',
          opacity: visible ? 1 : 0.28,
          transition: reduced ? undefined : 'opacity 240ms, background 240ms, border-color 240ms',
        }}
      >
        <div className="flex items-center gap-2">
          <span
            className="inline-block h-3 w-3 flex-shrink-0 rounded-full"
            style={{ background: color }}
            aria-hidden
          />
          <div className="leading-tight">
            <div className="text-sm font-semibold text-ws-ink">{label}</div>
            <div className="font-mono text-[11px] text-ws-ink-3">{formula}</div>
          </div>
        </div>
        <div className="font-mono text-xl font-bold tabular-nums" style={{ color: lit ? color : 'var(--ws-ink)' }}>
          {visible ? value : '—'}
        </div>
      </div>
    );
  };

  const protonColor = 'var(--ws-accent)';
  const neutronColor = 'var(--ws-ink-3)';
  const electronColor = '#3B82C4';

  return (
    <VizCard
      eyebrow="Atomic structure · Isotope notation"
      title={config.title ?? `Nuclear symbol of ${el.name}`}
      subtitle="Mass number A (top), atomic number Z (bottom) — and what they count"
      reducedMotion={reduced}
      hint={hasCue ? undefined : 'Tap the symbol to reveal every part'}
    >
      <div className="relative flex w-full flex-col gap-4">
        <NowYouTry show={showTry && !reduced && !hasCue} />

        <div className="grid gap-4 sm:grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)]">
          {/* The nuclear symbol notation, drawn as SVG so A/Z sit precisely. */}
          <button
            type="button"
            onClick={reveal}
            aria-label={`Reveal the parts of ${el.name}-${a}`}
            className="group grid place-items-center rounded-md border border-ws-border bg-ws-surface-2 px-2 py-6 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ws-accent"
          >
            <svg viewBox="0 0 220 200" className="h-auto w-full max-w-[260px]" role="img" aria-label={`Isotope notation ${a} over ${z} ${el.symbol}${chLabel}`}>
              {/* A — mass number, top-left superscript */}
              <text
                x="62"
                y="62"
                textAnchor="end"
                className="font-mono"
                style={{
                  fontSize: 34,
                  fontWeight: 700,
                  fill: isLit('a', 0) ? 'var(--ws-accent)' : 'var(--ws-ink-4)',
                  opacity: shown(0) ? 1 : 0.25,
                  transition: reduced ? undefined : 'fill 240ms, opacity 240ms',
                }}
              >
                {a}
              </text>
              {/* Z — atomic number, bottom-left subscript */}
              <text
                x="62"
                y="138"
                textAnchor="end"
                className="font-mono"
                style={{
                  fontSize: 34,
                  fontWeight: 700,
                  fill: isLit('z', 1) ? 'var(--ws-accent)' : 'var(--ws-ink-4)',
                  opacity: shown(1) ? 1 : 0.25,
                  transition: reduced ? undefined : 'fill 240ms, opacity 240ms',
                }}
              >
                {z}
              </text>
              {/* The element symbol */}
              <text
                x="74"
                y="120"
                textAnchor="start"
                style={{ fontSize: 80, fontWeight: 700, fill: 'var(--ws-ink)' }}
              >
                {el.symbol}
              </text>
              {/* Charge superscript, to the right of the symbol */}
              {chLabel && (
                <text
                  x="196"
                  y="64"
                  textAnchor="start"
                  className="font-mono"
                  style={{ fontSize: 24, fontWeight: 700, fill: 'var(--ws-ink-2)' }}
                >
                  {chLabel}
                </text>
              )}
              {/* Caption */}
              <text x="110" y="180" textAnchor="middle" style={{ fontSize: 13, fill: 'var(--ws-ink-3)' }}>
                {el.name}-{a}
              </text>
            </svg>
          </button>

          {/* The labelled count breakdown. */}
          <div className="flex flex-col gap-2">
            <CountRow part="protons" revealIdx={2} label="Protons" value={protons} formula={`= Z = ${z}`} color={protonColor} />
            <CountRow part="neutrons" revealIdx={3} label="Neutrons" value={neutrons} formula={`= A − Z = ${a} − ${z}`} color={neutronColor} />
            <CountRow
              part="electrons"
              revealIdx={2}
              label="Electrons"
              value={electrons}
              formula={charge ? `= Z − charge = ${z} − (${charge})` : `= Z = ${z} (neutral)`}
              color={electronColor}
            />
          </div>
        </div>

        {/* A small nucleus drawing of packed protons + neutrons. */}
        <div className="flex items-center gap-4 rounded-md border border-ws-border bg-ws-surface-2 px-3 py-3">
          <svg viewBox="-26 -26 52 52" className="h-24 w-24 flex-shrink-0" role="img" aria-label={`Nucleus: ${protons} protons and ${neutrons} neutrons`}>
            {nucleus.map((p, i) => {
              const isP = p.kind === 'p';
              const part: Part = isP ? 'protons' : 'neutrons';
              const lit = highlights.length ? isLit(part, isP ? 2 : 3) : true;
              const visible = shown(isP ? 2 : 3);
              return (
                <circle
                  key={i}
                  cx={p.x}
                  cy={p.y}
                  r={3.4}
                  fill={isP ? protonColor : neutronColor}
                  stroke={DARK_INK}
                  strokeOpacity={0.25}
                  strokeWidth={0.4}
                  style={{
                    opacity: visible ? (lit ? 1 : 0.25) : 0,
                    transition: reduced ? undefined : `opacity 220ms ${Math.min(i * 12, 600)}ms`,
                  }}
                />
              );
            })}
          </svg>
          <div className="min-w-0 text-xs text-ws-ink-2">
            <div className="mb-1 font-semibold text-ws-ink">Nucleus</div>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
              <span className="inline-flex items-center gap-1.5">
                <span className="inline-block h-2.5 w-2.5 rounded-full" style={{ background: protonColor }} aria-hidden />
                {protons} proton{protons === 1 ? '' : 's'}
              </span>
              <span className="inline-flex items-center gap-1.5">
                <span className="inline-block h-2.5 w-2.5 rounded-full" style={{ background: neutronColor }} aria-hidden />
                {neutrons} neutron{neutrons === 1 ? '' : 's'}
              </span>
            </div>
            <p className="mt-1.5 text-[11px] text-ws-ink-3">
              Mass number A = protons + neutrons = {protons} + {neutrons} = {a}.
            </p>
          </div>
        </div>
      </div>
    </VizCard>
  );
}
